import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getAttendanceSummary,
  getAttendanceDailyTrend,
  getAttendanceDepartmentBreakdown,
  getAttendanceLateDistribution,
  getAttendanceTopLate,
  getAttendanceTopUndertime,
  getAttendanceBradford,
  getAttendanceWarnings,
} from '../../../services/attendanceDashboardService';
import { getDepartments, getDepartment } from '../../../services/departmentService';
import { getLocations, getLocation } from '../../../services/locationService';
import { getUserGroups, getUserGroup } from '../../../services/userGroupService';
import { getEmployeeStatuses, getEmployeeStatus } from '../../../services/employeeStatusService';
import { todayIso } from '../clock';
import { toQuery } from '../format';

// Kart başına bir uç; 8 dilim paralel yüklenir.
const SLICES = {
  summary: getAttendanceSummary,
  trend: getAttendanceDailyTrend,
  departments: getAttendanceDepartmentBreakdown,
  lateDistribution: getAttendanceLateDistribution,
  topLate: getAttendanceTopLate,
  topUndertime: getAttendanceTopUndertime,
  bradford: getAttendanceBradford,
  warnings: getAttendanceWarnings,
};

const initialSlices = () => Object.keys(SLICES).reduce((acc, key) => {
  acc[key] = { data: null, loading: true, failed: false };
  return acc;
}, {});

// Seçenek uçları sayfalı PaginatedList döner; dizi gelirse olduğu gibi.
const readList = (res) => {
  const payload = res?.data?.data;
  if (Array.isArray(payload)) return payload;
  return payload?.data ?? [];
};

const LOOKUP_PAGE_SIZE = 20;

// Aranabilir süzgeç seçenekleri (inceleme M9): ilk 200 kayıtla sınırlı açılır liste yerine sunucuda ad araması
// (name[like]) + seçili değerin adını kimlikle çözme — binlerce departman/lokasyon/grup olan kiracıda da her seçenek bulunur.
export const makeLookup = (fetchList, fetchOne, { ordered = true } = {}) => ({
  onSearch: async (query) => {
    const params = { pageSize: LOOKUP_PAGE_SIZE };
    if (query) params['name[like]'] = query;
    if (ordered) params.orderBy = 'name';
    return readList(await fetchList(params));
  },
  onGetById: async (id) => {
    const res = await fetchOne(id);
    return res?.data?.status === false ? null : (res?.data?.data ?? null);
  },
});

const LOOKUPS = {
  departments: makeLookup(getDepartments, getDepartment),
  locations: makeLookup(getLocations, getLocation),
  groups: makeLookup(getUserGroups, getUserGroup),
  // Statü ucu orderBy almaz — kendisi SortOrder, Name sıralar.
  statuses: makeLookup(getEmployeeStatuses, getEmployeeStatus, { ordered: false }),
};

/**
 * Devam Paneli veri kancası.
 * - filters: dönem (ay anahtarı 'yyyy-MM' | 'custom' + customFrom/customTo) + statü / grup / departman / lokasyon.
 * - query: toQuery(filters) — özel aralık geçersizse null ve istek ATILMAZ.
 * - slices: her kart { data, loading, failed }. api.js GET'te reddetmez → res.data.status === false = başarısız.
 *   Yükleme başlarken önceki veri tutulur (çizim soluk kalır); istek sıra numarası eski yanıtın yenisini ezmesini önler.
 */
export function useAttendanceDashboardApi() {
  const [filters, setFilters] = useState(() => ({
    periodKey: todayIso().slice(0, 7),
    customFrom: null,
    customTo: null,
    employeeStatusId: null,
    userGroupId: null,
    departmentId: null,
    locationId: null,
  }));
  const query = useMemo(() => toQuery(filters, todayIso()), [filters]);
  const queryKey = query ? JSON.stringify(query) : null;

  const [slices, setSlices] = useState(initialSlices);
  const [statusesAvailable, setStatusesAvailable] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!queryKey) return;
    const activeQuery = JSON.parse(queryKey);
    seq.current += 1;
    const mine = seq.current;

    setSlices((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => { next[key] = { ...prev[key], loading: true, failed: false }; });
      return next;
    });

    Object.entries(SLICES).forEach(([key, fetcher]) => {
      Promise.resolve()
        .then(() => fetcher(activeQuery))
        .then((res) => (res?.data && res.data.status !== false
          ? { data: res.data.data ?? null, loading: false, failed: false }
          : { data: null, loading: false, failed: true }))
        .catch(() => ({ data: null, loading: false, failed: true }))
        .then((next) => {
          if (seq.current !== mine) return;   // daha yeni bir istek var — eski yanıt yenisini ezmez
          setSlices((prev) => ({ ...prev, [key]: next }));
        });
    });
  }, [queryKey]);

  // Statü süzgeci yalnız kiracıda personel statüsü tanımlıysa gösterilir (tek kayıtlık bakış).
  useEffect(() => {
    let alive = true;
    Promise.resolve()
      .then(() => getEmployeeStatuses({ pageSize: 1 }))
      .then((res) => {
        const payload = res?.data?.data;
        const total = Array.isArray(payload) ? payload.length : (payload?.totalRowCount ?? readList(res).length);
        if (alive) setStatusesAvailable(total > 0);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Özel aralık seçilmiş ama tarihleri eksik/geçersiz: istek atılmaz; sayfa "tarih seçin" durumunu gösterir (M7).
  const awaitingRange = filters.periodKey === 'custom' && !query;

  return {
    filters, setFilters, query, slices, lookups: LOOKUPS, statusesAvailable, awaitingRange,
  };
}
